/**
 * Stock notification modal component
 * Handles form submission and data population for stock notification requests
 *
 * This component integrates with Shopify's customer form system and Shopify Flow
 * to automatically notify customers when products come back in stock.
 */

import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that manages the stock notification modal
 *
 * @extends Component
 */
class StockNotificationModal extends Component {
  requiredRefs = ['dialogComponent'];
  #boundHandleTriggerClick;
  #boundHandleFormSubmit;
  #boundOnVariantSelected;
  #boundOnVariantUpdate;

  connectedCallback() {
    super.connectedCallback();

    // Store bound methods for proper cleanup
    this.#boundHandleTriggerClick = this.#handleTriggerClick.bind(this);
    this.#boundHandleFormSubmit = this.#handleFormSubmit.bind(this);
    this.#boundOnVariantSelected = this.#onVariantSelected.bind(this);
    this.#boundOnVariantUpdate = this.#onVariantUpdate.bind(this);

    // Listen for modal trigger events from notification buttons
    document.addEventListener('click', this.#boundHandleTriggerClick);

    // Listen for form submission
    const form = this.querySelector('.stock-notification-modal__form');
    if (form) {
      form.addEventListener('submit', this.#boundHandleFormSubmit);
    }

    // Listen for variant selection/update events to disable/enable notify me button
    // Events bubble up from product form sections, so listen on document
    document.addEventListener(ThemeEvents.variantSelected, this.#boundOnVariantSelected);
    document.addEventListener(ThemeEvents.variantUpdate, this.#boundOnVariantUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    // Remove all event listeners using stored bound references
    document.removeEventListener('click', this.#boundHandleTriggerClick);

    const form = this.querySelector('.stock-notification-modal__form');
    if (form) {
      form.removeEventListener('submit', this.#boundHandleFormSubmit);
    }

    document.removeEventListener(ThemeEvents.variantSelected, this.#boundOnVariantSelected);
    document.removeEventListener(ThemeEvents.variantUpdate, this.#boundOnVariantUpdate);
  }

  /**
   * Disables the notify me button while variants are being updated
   */
  #onVariantSelected = () => {
    // Find notify me button within the context of the product form that triggered the event
    // The event bubbles up, so we can find the button in the same section
    const notifyMeButton = document.querySelector('[data-modal-trigger="stock-notification-modal"]:not([hidden])');
    if (notifyMeButton instanceof HTMLButtonElement) {
      notifyMeButton.disabled = true;
    }
  };

  /**
   * Re-enables the notify me button after variant update is complete
   */
  #onVariantUpdate = () => {
    // Find notify me button and re-enable it
    const notifyMeButton = document.querySelector('[data-modal-trigger="stock-notification-modal"]:not([hidden])');
    if (notifyMeButton instanceof HTMLButtonElement) {
      notifyMeButton.disabled = false;
    }
  };

  /**
   * Handles clicks on notification trigger buttons
   * @param {MouseEvent} event - The click event
   */
  #handleTriggerClick(event) {
    const trigger = event.target.closest('[data-modal-trigger="stock-notification-modal"]');
    if (!trigger) return;

    event.preventDefault();

    // // Populate modal with product/variant data
    // this.#populateModalData(trigger);

    // // Show the modal
    // this.showDialog();

    const bisFrame = document.getElementById('BIS_frame');
    const variantHandle = trigger.dataset.variantHandle;
    const variantId = trigger.dataset.variantId; // Get the numeric variant ID
    const variantTitle = trigger.dataset.variantTitle; // Get the original title for text matching

    if (bisFrame) {
      const applySelection = () => {
        try {
          const doc = bisFrame.contentDocument || bisFrame.contentWindow?.document;
          if (!doc) return;

          const variantsSelect = doc.querySelector('#variants');
          if (variantsSelect) {
            // First try to match by variantId (numeric) - most reliable
            const optionByValue = Array.from(variantsSelect.options).find((option) => option.value === variantId);

            if (optionByValue) {
              variantsSelect.value = variantId;
              variantsSelect.dispatchEvent(new Event('change', { bubbles: true }));
              return;
            }

            // Fallback: match by text content (variant title)
            // This handles cases where variantId doesn't match exactly
            const optionByText = Array.from(variantsSelect.options).find(
              (option) => option.textContent.trim() === variantTitle?.trim()
            );

            if (optionByText) {
              variantsSelect.value = optionByText.value;
              variantsSelect.dispatchEvent(new Event('change', { bubbles: true }));
              return;
            }

            console.warn('Could not find matching option', {
              variantId,
              variantTitle,
              variantHandle,
              availableOptions: Array.from(variantsSelect.options).map((opt) => ({
                value: opt.value,
                text: opt.textContent.trim(),
              })),
            });
          }
        } catch (e) {
          // Cross-origin: cannot access iframe DOM
          console.log('Cross-origin: cannot access iframe DOM', e);
        }
      };

      if (bisFrame.contentWindow?.document?.readyState === 'complete') {
        applySelection();
      } else {
        bisFrame.addEventListener('load', applySelection, { once: true });
      }
    }

    // Please get element #BIS_trigger and trigger click on it
    const bisTrigger = document.getElementById('BIS_trigger');
    if (bisTrigger) {
      bisTrigger.click();
    }
  }

  /**
   * Populates modal with product and variant information
   * @param {HTMLElement} trigger - The trigger button element
   */
  #populateModalData(trigger) {
    const productId = trigger.dataset.productId;
    const variantId = trigger.dataset.variantId;
    const productTitle = trigger.dataset.productTitle;
    const variantTitle = trigger.dataset.variantTitle;
    const productHandle = trigger.dataset.productHandle;
    const variantHandle = trigger.dataset.variantHandle;

    // Update variant display in description
    const variantSpan = this.querySelector('.stock-notification-modal__variant');
    if (variantSpan) {
      variantSpan.textContent = variantTitle;
    }

    // Update hidden form fields for Flow processing
    const bodyField = this.querySelector('.stock-notification-modal__body-field');
    if (bodyField) {
      bodyField.value = `PRODUCT_ID:${productId},VARIANT_ID:${variantId},PRODUCT_TITLE:${productTitle},VARIANT_TITLE:${variantTitle}`;
    }

    // Store data for form submission
    this.dataset.productId = productId;
    this.dataset.variantId = variantId;
    this.dataset.productTitle = productTitle;
    this.dataset.variantTitle = variantTitle;
    this.dataset.productHandle = productHandle;
    this.dataset.variantHandle = variantHandle;

    // Clear any previous form state
    this.#clearFormState();
  }

  /**
   * Handles form submission
   * @param {Event} event - The form submit event
   */
  #handleFormSubmit(event) {
    const form = event.target;
    const email = form.querySelector('input[name="contact[email]"]').value;

    // Update the body field with email information
    const bodyField = form.querySelector('.stock-notification-modal__body-field');
    if (bodyField && this.dataset.productId && this.dataset.variantId) {
      bodyField.value += `,EMAIL:${email}`;
    }

    // Add three-level tag hierarchy for flexible filtering and analysis
    // Level 1: General stock notification tag
    // Level 2: Product-specific interest tag
    // Level 3: Variant-specific interest tag
    const tagField = form.querySelector('input[name="contact[tags]"]');
    if (tagField && this.dataset.productHandle && this.dataset.variantHandle) {
      const tags = [
        'stock-notify',
        `stock-notify:${this.dataset.productHandle}`,
        `stock-notify:${this.dataset.productHandle}:${this.dataset.variantHandle}`,
      ];
      tagField.value = tags.join(', ');
    }

    // Update subject with product information
    const subjectField = form.querySelector('input[name="contact[subject]"]');
    if (subjectField && this.dataset.productTitle && this.dataset.variantTitle) {
      subjectField.value = `Stock Notification: ${this.dataset.productTitle} - ${this.dataset.variantTitle}`;
    }
  }

  /**
   * Clears form state when modal opens
   */
  #clearFormState() {
    const form = this.querySelector('.stock-notification-modal__form');
    if (form) {
      // Reset form fields
      const emailInput = form.querySelector('input[name="contact[email]"]');
      if (emailInput) {
        emailInput.value = '';
        emailInput.removeAttribute('aria-invalid');
      }

      // Clear error/success messages
      const errorMessage = form.querySelector('.stock-notification-modal__error');
      const successMessage = form.querySelector('.stock-notification-modal__success');

      if (errorMessage) {
        errorMessage.style.display = 'none';
      }
      if (successMessage) {
        successMessage.style.display = 'none';
      }
    }
  }

  /**
   * Shows the dialog modal
   * Delegates to the dialog-component
   */
  showDialog() {
    const dialogComponent = this.refs.dialogComponent;
    if (dialogComponent) {
      dialogComponent.showDialog();
    }
  }

  /**
   * Closes the dialog modal
   * Delegates to the dialog-component
   */
  closeDialog() {
    const dialogComponent = this.refs.dialogComponent;
    if (dialogComponent) {
      dialogComponent.closeDialog();
    }
  }
}

// Register the custom element
if (!customElements.get('stock-notification-modal')) {
  customElements.define('stock-notification-modal', StockNotificationModal);
}

export { StockNotificationModal };
